import React, { useState, useMemo, useEffect } from "react";
import { api, OpsTodo } from "../api";
import { KpiCard } from "../components/KpiCard";
import { GlassPanel } from "../components/GlassPanel";
import { Badge } from "../components/Badge";
import { Modal } from "../components/Modal";
import { SkeletonCard, ErrorState, toast } from "../components";

// --- Types ---
type Category = OpsTodo["category"];
type Priority = OpsTodo["priority"];
type Status = OpsTodo["status"];

type TabKey = "today" | "upcoming" | "overdue" | "done" | "all";

// --- Constants ---
const CATEGORY_ICONS: Record<Category, string> = {
  pack: "inventory_2",
  ship: "local_shipping",
  stock: "warehouse",
  call: "phone",
  print: "print",
  other: "task",
};

const CATEGORY_LABELS: Record<Category, string> = {
  pack: "Đóng gói",
  ship: "Giao hàng",
  stock: "Nhập kho",
  call: "Gọi NCC",
  print: "In tem",
  other: "Khác",
};

const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: "Gấp",
  normal: "Bình thường",
  low: "Thấp",
};

const PRIORITY_VARIANT: Record<Priority, "bad" | "warn" | "ghost"> = {
  urgent: "bad",
  normal: "warn",
  low: "ghost",
};

const TABS: { key: TabKey; label: string }[] = [
  { key: "today", label: "Hôm nay" },
  { key: "upcoming", label: "Sắp đến hạn" },
  { key: "overdue", label: "Quá hạn" },
  { key: "done", label: "Đã xong" },
  { key: "all", label: "Tất cả" },
];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function startOfWeek(): string {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
}

// --- Component ---
export default function NeedsCompletionPage() {
  const [todos, setTodos] = useState<OpsTodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("today");
  const [catFilter, setCatFilter] = useState<Category | "">("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.ops.todos.list();
      setTodos(r.todos || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const today = todayStr();
  const weekStart = startOfWeek();

  const filtered = useMemo(() => {
    let list = todos;

    switch (tab) {
      case "today":
        list = list.filter((t) => t.status !== "done" && (t.due_date || "") === today);
        break;
      case "upcoming":
        list = list.filter((t) => t.status !== "done" && (t.due_date || "") > today);
        break;
      case "overdue":
        list = list.filter((t) => t.status !== "done" && t.due_date && t.due_date < today);
        break;
      case "done":
        list = list.filter((t) => t.status === "done");
        break;
      case "all":
        break;
    }

    if (catFilter) {
      list = list.filter((t) => t.category === catFilter);
    }

    list = [...list].sort((a, b) => {
      const pOrder: Record<Priority, number> = { urgent: 0, normal: 1, low: 2 };
      if (pOrder[a.priority] !== pOrder[b.priority]) return pOrder[a.priority] - pOrder[b.priority];
      const ad = a.due_date || "";
      const bd = b.due_date || "";
      if (ad && bd) return ad.localeCompare(bd);
      if (ad) return -1;
      if (bd) return 1;
      return 0;
    });

    return list;
  }, [todos, tab, catFilter, today]);

  const pending = todos.filter((t) => t.status !== "done").length;
  const overdue = todos.filter((t) => t.status !== "done" && t.due_date && t.due_date < today).length;
  const doneThisWeek = todos.filter((t) => t.status === "done" && (t.completed_at || "") >= weekStart).length;

  const updateStatus = async (id: string, status: Status) => {
    try {
      await api.ops.todos.update(id, { status });
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const deleteTodo = async (id: string) => {
    try {
      await api.ops.todos.delete(id);
      toast.success("Đã xóa");
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const addTodo = async (item: {
    title: string;
    description: string;
    category: Category;
    priority: Priority;
    due_date: string;
  }) => {
    try {
      await api.ops.todos.create({
        title: item.title,
        description: item.description || undefined,
        category: item.category,
        priority: item.priority,
        due_date: item.due_date || undefined,
      });
      toast.success("Đã thêm việc");
      setShowAdd(false);
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  return (
    <>
      {/* KPI Cards */}
      <div className="kpi-grid">
        <KpiCard label="Đang chờ" value={pending} dotColor={pending > 0 ? "warn" : "ok"} />
        <KpiCard label="Quá hạn" value={overdue} dotColor={overdue > 0 ? "bad" : "ok"} />
        <KpiCard label="Hoàn thành tuần này" value={doneThisWeek} dotColor="blue" />
      </div>

      {/* Filter bar */}
      <GlassPanel className="mb-16">
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            className="input"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value as Category | "")}
            style={{ maxWidth: 180 }}
          >
            <option value="">Tất cả loại</option>
            {(Object.keys(CATEGORY_LABELS) as Category[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            Thêm việc
          </button>
        </div>
      </GlassPanel>

      {/* Tab nav */}
      <div className="tab-row mb-16">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === "overdue" && overdue > 0 && (
              <span className="badge bad" style={{ marginLeft: 6, fontSize: 11 }}>{overdue}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} height={88} />)}
        </div>
      ) : error ? (
        <ErrorState error={error} onRetry={load} />
      ) : (
        <div className="todo-card-list">
          {filtered.length === 0 && (
            <div className="empty-state">
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.4 }}>check_circle</span>
              <p>Không có việc nào. Mọi thứ đều ổn.</p>
            </div>
          )}
          {filtered.map((item) => (
            <TodoCard
              key={item.id}
              item={item}
              today={today}
              onStart={() => updateStatus(item.id, "in_progress")}
              onDone={() => updateStatus(item.id, "done")}
              onDelete={() => deleteTodo(item.id)}
            />
          ))}
        </div>
      )}

      {/* Add Modal */}
      <AddTodoModal open={showAdd} onClose={() => setShowAdd(false)} onAdd={addTodo} />
    </>
  );
}

// --- TodoCard ---
function TodoCard({
  item,
  today,
  onStart,
  onDone,
  onDelete,
}: {
  item: OpsTodo;
  today: string;
  onStart: () => void;
  onDone: () => void;
  onDelete: () => void;
}) {
  const isOverdue = !!item.due_date && item.due_date < today && item.status !== "done";

  return (
    <div className={`todo-card ${isOverdue ? "todo-card--overdue" : ""} ${item.status === "done" ? "todo-card--done" : ""}`}>
      <div className="todo-card__icon">
        <span className="material-symbols-outlined">{CATEGORY_ICONS[item.category]}</span>
      </div>
      <div className="todo-card__body">
        <div className="todo-card__title">{item.title}</div>
        {item.description && (
          <div className="todo-card__desc">{item.description.length > 80 ? item.description.slice(0, 80) + "…" : item.description}</div>
        )}
        <div className="todo-card__meta">
          <Badge variant={PRIORITY_VARIANT[item.priority]}>{PRIORITY_LABELS[item.priority]}</Badge>
          <Badge variant="info">{CATEGORY_LABELS[item.category]}</Badge>
          {item.due_date && (
            <span className={`todo-card__due ${isOverdue ? "overdue" : ""}`}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>calendar_today</span>
              {item.due_date}
            </span>
          )}
          {item.status === "in_progress" && <Badge variant="warn">Đang làm</Badge>}
          {item.status === "done" && <Badge variant="ok">Xong</Badge>}
        </div>
      </div>
      <div className="todo-card__actions">
        {item.status === "pending" && (
          <button className="btn btn-sm" onClick={onStart} title="Bắt đầu">
            <span className="material-symbols-outlined">play_arrow</span>
          </button>
        )}
        {item.status !== "done" && (
          <button className="btn btn-sm btn-ok" onClick={onDone} title="Xong">
            <span className="material-symbols-outlined">check</span>
          </button>
        )}
        <button className="btn btn-sm btn-ghost" onClick={onDelete} title="Xoá">
          <span className="material-symbols-outlined">delete</span>
        </button>
      </div>
    </div>
  );
}

// --- Add Modal ---
function AddTodoModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (item: { title: string; description: string; category: Category; priority: Priority; due_date: string }) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<Category>("other");
  const [priority, setPriority] = useState<Priority>("normal");
  const [dueDate, setDueDate] = useState("");

  const reset = () => {
    setTitle("");
    setDescription("");
    setCategory("other");
    setPriority("normal");
    setDueDate("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onAdd({ title: title.trim(), description: description.trim(), category, priority, due_date: dueDate });
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Thêm việc cần làm" width={520}>
      <form onSubmit={handleSubmit} className="form-stack">
        <div className="form-group">
          <label className="form-label">Tiêu đề *</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Đóng gói đơn #1234" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Mô tả</label>
          <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Chi tiết (tuỳ chọn)" />
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Loại</label>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {(Object.keys(CATEGORY_LABELS) as Category[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Ưu tiên</label>
            <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {(Object.keys(PRIORITY_LABELS) as Priority[]).map((p) => (
                <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Hạn hoàn thành</label>
          <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} inputMode="numeric" />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <button type="button" className="btn" onClick={handleClose}>Huỷ</button>
          <button type="submit" className="btn btn-primary" disabled={!title.trim()}>Thêm</button>
        </div>
      </form>
    </Modal>
  );
}
